import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Gift, Home, Palette, Heart, Coffee, Laptop, Book, Baby, Scissors, Dumbbell, Watch } from 'lucide-react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useDeckState } from '@/hooks/useDeckState';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';

import { AIResultsHeader } from '@/components/browse/AIResultsHeader';
import ActiveFilters from '@/components/browse/ActiveFilters';
import ArchetypeWorld from '@/components/browse/ArchetypeWorld';
import ArchetypeWorldHeader from '@/components/browse/ArchetypeWorldHeader';
import QuestionnaireFilterBar from '@/components/browse/QuestionnaireFilterBar';
import DeckCard from '@/components/browse/DeckCard';
import EditorialRail from '@/components/browse/EditorialRail';
import FilterPanel from '@/components/FilterPanel';
import GridSkeleton from '@/components/browse/GridSkeleton';
import Footer from '@/components/Footer';
import HeroSection from '@/components/browse/HeroSection';
import ProductCard from '@/components/ProductCard';
import ProductModal from '@/components/ProductModal';
import StickyFilterBar from '@/components/browse/StickyFilterBar';
import UnifiedProductGrid from '@/components/browse/UnifiedProductGrid';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { usePrelaunch } from '@/hooks/usePrelaunch';
import { useFeatures } from '@/hooks/useFeatures';
import ComingSoon from '@/components/ComingSoon';
import { DEFAULT_ARCHETYPE_ID, ARCHETYPES as ARCHETYPES_FALLBACK, getEnabledArchetypes, getArchetype, normalizeArchetype } from '@/config/archetypes';

import { logger } from '@/utils/logger';
const fetcher = (url) => api.get(url).then(res => res.data);

export default function Browse({ user }) {
  // Dark mode
  const darkMode = useDarkMode();
  const { prelaunch } = usePrelaunch(user);

  useDocumentMeta({
    title: 'Browse Gift Ideas, Mementa',
    description:
      'Discover thoughtfully curated gift ideas for every occasion, interest, and budget. Filter by category, price, and personality.',
    url: 'https://mementa.gifts/browse',
  });
  const location = useLocation();
  
  // Data fetching
  const { data: products = [], isLoading: productsLoading } = useSWR('/products', fetcher, { revalidateOnFocus: true, dedupingInterval: 2000 });
  const { data: browseSections = [] } = useSWR('/browse-sections', fetcher);
  const { data: featuredSections = [] } = useSWR('/featured-sections', fetcher);
  const { data: decks = [] } = useSWR('/decks', fetcher);
  const { data: archetypesRaw } = useSWR('/archetypes', fetcher, { revalidateOnFocus: false });

  // Hydrated archetype list: prefer backend truth, fall back to static seed.
  const archetypes = useMemo(() => {
    if (Array.isArray(archetypesRaw) && archetypesRaw.length) {
      return archetypesRaw.map(normalizeArchetype).filter(Boolean).sort((a, b) => a.order - b.order);
    }
    return ARCHETYPES_FALLBACK;
  }, [archetypesRaw]);
  const enabledArchetypes = useMemo(() => getEnabledArchetypes(archetypes), [archetypes]);

  // Feature flags (Admin -> Settings -> Feature Toggles). Admins always bypass.
  const { features } = useFeatures();
  const worldsEnabled = !!user?.is_admin || !!features.archetype_worlds;
  const visibleArchetypes = useMemo(() => {
    if (!worldsEnabled) return [];
    if (user?.is_admin || features.brand_directory) return enabledArchetypes;
    return enabledArchetypes.filter((a) => a.slug !== 'support_a_brand');
  }, [worldsEnabled, enabledArchetypes, features.brand_directory, user]);

  // Deck state
  const deckState = useDeckState();
  
  // Local state
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAIResults, setShowAIResults] = useState(false);
  const [aiContext, setAiContext] = useState({ personalitySummary: '', recipientName: '' });
  const [aiRecommendationsMap, setAiRecommendationsMap] = useState(new Map());
  const aiSeenIdsRef = useRef(new Set()); // Maps product_id to {reason, confidence}
  const [creativeAlternatives, setCreativeAlternatives] = useState([]); // For "Worth Considering" section
  const [filters, setFilters] = useState({ category: '', priceRange: '', merchant: [], tags: [], searchQuery: '' });
  const [sortBy, setSortBy] = useState('');
  // "Browse All" view toggle. When ON, the category dividers are removed and
  // every product renders in one continuous grid. When OFF (default) the grid
  // is organized into category sections. Mutually exclusive with a single
  // category filter, turning Browse All on clears any active category.
  const [browseAll, setBrowseAll] = useState(false);

  // Active gift archetype ("world"). Slug-keyed and synced to the ?archetype=
  // URL param so state survives reloads and shared links work.
  const [activeArchetype, setActiveArchetype] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_ARCHETYPE_ID;
    const params = new URLSearchParams(window.location.search);
    return params.get('archetype') || DEFAULT_ARCHETYPE_ID;
  });

  // When the archetype list finally hydrates from the backend, drop back to
  // the default if the URL requested an archetype that no longer exists
  // (e.g. legacy ?archetype=keepsakes links) or is hidden by a feature flag.
  useEffect(() => {
    if (!visibleArchetypes.length) return;
    const enabledSlugs = visibleArchetypes.map((a) => a.slug);
    if (!enabledSlugs.includes(activeArchetype)) {
      setActiveArchetype(DEFAULT_ARCHETYPE_ID);
    }
  }, [visibleArchetypes, activeArchetype]);

  const currentArchetype = useMemo(
    () => getArchetype(activeArchetype, archetypes),
    [activeArchetype, archetypes]
  );

  const handleArchetypeChange = useCallback((slug) => {
    setActiveArchetype(slug);
    const url = new URL(window.location.href);
    if (slug === DEFAULT_ARCHETYPE_ID) {
      url.searchParams.delete('archetype');
    } else {
      url.searchParams.set('archetype', slug);
    }
    window.history.replaceState({}, '', url.toString());
  }, []);

  const [loadingMoreLikeThis, setLoadingMoreLikeThis] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  useScrollLock(loadingPreferences);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);

  // When a "Curated for you" tile is clicked, the corresponding featured
  // section gets expanded into a full spotlight banner at the top of the
  // content area. Click again (or close) to dismiss. This lives outside
  // the filter state because it's a discovery surface, not a refinement.
  const [spotlightFeatured, setSpotlightFeatured] = useState(null);

  // Scroll-to anchors keyed by category name (populated by category dividers
  // inside the UnifiedProductGrid). Used by the EditorialRail click handlers
  // to jump to a deck or featured section's location in the grid below.
  const scrollAnchors = useRef({});
  
  // Ref to prevent duplicate effect execution in StrictMode
  const navigationProcessedRef = useRef(false);

  const handlePreferencesSearch = useCallback(async (recipientName, profileData) => {
    setLoadingPreferences(true);
    try {
      // Call AI recommendations API with full profile (backend handles conversion)
      const response = await api.post('/ai-recommendations', { recipientProfile: profileData });
      
      // Process recommendations
      setShowAIResults(true); 
      setAiContext({ personalitySummary: `Gift recommendations for ${recipientName}`, recipientName });
      
      // Create a map of product_id to AI metadata
      const aiMap = new Map();
      response.data.recommendations.forEach(rec => {
        const product = rec.product || rec;
        if (product.id) {
          aiMap.set(product.id, {
            reason: rec.reason || '',
            confidence: rec.confidence || 'medium'
          });
        }
      });
      setAiRecommendationsMap(aiMap);
      
      // Process creative alternatives
      const alternatives = response.data.creative_alternatives || [];
      setCreativeAlternatives(alternatives);
      
      // Extract product data: top picks first, then EVERY other relevant
      // match (ranked) so nothing that applies is hidden.
      const topPicks = response.data.recommendations.map(rec => rec.product || rec);
      const moreMatches = (response.data.all_matches || []).map(m => m.product);
      const productList = [...topPicks, ...moreMatches];
      setFilteredProducts(productList);
      aiSeenIdsRef.current = new Set(productList.map(p => p.id));

      // Show success toast with tier counts
      toast.success(moreMatches.length > 0
        ? `✨ ${topPicks.length} top picks + ${moreMatches.length} more matches for ${recipientName}!`
        : `✨ ${topPicks.length} personalized recommendations for ${recipientName}!`);
      
      setTimeout(() => {
        window.scrollTo({ top: document.querySelector('[data-browse-content]')?.offsetTop - 100, behavior: 'smooth' }); 
      }, 100);
      
    } catch (error) {
      logger.error('Error getting recommendations from profile:', error);
      toast.error('Failed to generate recommendations');
    } finally {
      setLoadingPreferences(false);
    }
  }, []);
  
  // Check if navigated from keepsake preferences or add gift mode
  useEffect(() => {
    // Prevent duplicate execution in StrictMode
    if (navigationProcessedRef.current) return;
    
    if (location.state?.fromPreferences && location.state?.recipientProfile) {
      navigationProcessedRef.current = true;
      const { recipientName, recipientProfile } = location.state;
      handlePreferencesSearch(recipientName, recipientProfile);
      // Clear the state so it doesn't retrigger
      window.history.replaceState({}, document.title);
    } else if (location.state?.addGiftMode) {
      navigationProcessedRef.current = true;
      const { recipientName } = location.state;
      // Add-gift mode now just shows the standard unified grid, the user
      // can pick anything; the toast contextualizes why they're here.
      toast.success(`Browse gifts for ${recipientName}`);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, handlePreferencesSearch]);
  
  const sortOptions = [
    { value: 'name-az', label: 'Name: A–Z', icon: '🔤' },
    { value: 'price-low', label: 'Price: Low to High', icon: '💰' },
    { value: 'price-high', label: 'Price: High to Low', icon: '💎' }
  ];

  const getIconForSection = (section) => {
    const iconMap = { 'Home & Living': Home, 'Fashion & Style': Palette, 'Health & Wellness': Heart, 'Food & Beverage': Coffee, 'Tech & Gadgets': Laptop, 'Books & Learning': Book, 'Baby & Kids': Baby, 'Beauty & Personal Care': Scissors, 'Sports & Outdoors': Dumbbell, 'Jewelry & Watches': Watch };
    return iconMap[section.title] || Gift;
  };

  // Featured Section Renderer using DeckCard
  const renderFeaturedSection = (featured, position) => {
    const featuredItems = [];
    featured.item_ids?.forEach((itemId, itemIdx) => {
      const itemType = featured.item_types?.[itemIdx];
      if (itemType === 'product') {
        const product = products.find(p => p.id === itemId);
        if (product) featuredItems.push({ type: 'product', data: product });
      } else if (itemType === 'deck') {
        const deck = decks.find(d => d.id === itemId);
        if (deck) {
          const deckProds = products.filter(p => deck.product_ids?.includes(p.id));
          if (deckProds.length > 0) featuredItems.push({ type: 'deck', data: deck, products: deckProds });
        }
      }
    });
    
    if (featuredItems.length === 0) return null;
    
    return (
      <div key={`featured-${position}-${featured.id}`} className="mb-6 sm:mb-8 -mx-4 sm:mx-0 sm:rounded-2xl p-4 sm:p-8" style={{ background: darkMode.isDark ? 'linear-gradient(135deg, #2D4354 0%, #534145 100%)' : '#FFFFFF', border: '2px solid #FED7A5', boxShadow: darkMode.isDark ? '0 8px 32px rgba(45, 67, 84, 0.4)' : '0 8px 32px rgba(158, 103, 82, 0.15)' }}>
        <h3 className="text-center text-xl sm:text-2xl md:text-3xl mb-2 px-2" style={{fontFamily: 'Playfair Display, serif', color: darkMode.isDark ? '#FED7A5' : '#20212B', fontWeight: '700'}}>{featured.name}</h3>
        <p className="text-center text-sm sm:text-base mb-6 sm:mb-8 px-2" style={{color: darkMode.isDark ? '#E5E5E5' : '#534145', fontWeight: '500'}}>{featured.description}</p>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6" style={{overflow: 'visible'}}>
          {featuredItems.map((item, itemIdx) => {
            if (item.type === 'product') return <ProductCard key={`feat-prod-${item.data.id}`} product={item.data} user={user} />;
            const globalDeckIdx = `feat-${position}-${itemIdx}`;
            return (
              <DeckCard
                key={`feat-deck-${item.data.id}`}
                deck={item.data}
                products={item.products}
                deckId={globalDeckIdx}
                openDeckId={deckState.openDeckId}
                hoveredDeck={deckState.hoveredDeck}
                fannedDeckMobile={deckState.fannedDeckMobile}
                isMobile={deckState.isMobile}
                onMouseEnter={() => !deckState.isMobile.current && deckState.setHoveredDeck(globalDeckIdx)}
                onMouseLeave={() => !deckState.isMobile.current && deckState.setHoveredDeck(null)}
                onClick={(e) => deckState.handleDeckClick(globalDeckIdx, e)}
                onCloseDeck={deckState.closeDeck}
                onProductClick={deckState.handleProductClick}
                user={user}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // Stable pseudo-random shuffle for the "Browse All" flat view, products
  // appear in a mixed, random-looking order (an endless-store feel) that
  // stays fixed across re-renders (so the grid doesn't visibly reshuffle).
  const stableShuffle = (items) => {
    const hashId = (s) => {
      let h = 0;
      for (let i = 0; i < s.length; i += 1) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
      return h;
    };
    return [...items].sort((a, b) => hashId(a.id) - hashId(b.id));
  };

  // Filter logic, runs continuously and is the SINGLE source of truth for
  // what shows in the product grid. When no filters are active, this just
  // produces a sorted copy of the full catalog (sorted by category by
  // default so the grouped-by-category view in the grid reads naturally).
  const applyFilters = useCallback(() => {
    let filtered = [...products];

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(query) || (p.description && p.description.toLowerCase().includes(query)) || p.category.toLowerCase().includes(query) || (p.tags && p.tags.some(tag => tag.toLowerCase().includes(query))) || (p.merchant && p.merchant.toLowerCase().includes(query)));
    }
    if (filters.category) filtered = filtered.filter(p => p.category === filters.category);
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(p => p.price >= min && (max ? p.price <= max : true));
    }
    if (filters.merchant.length > 0) filtered = filtered.filter(p => filters.merchant.includes(p.merchant));
    if (filters.tags.length > 0) filtered = filtered.filter(p => p.tags && filters.tags.some(tag => p.tags.includes(tag)));

    // Sort. Default (no sortBy) groups by category so the grid's category
    // dividers read in alphabetical order. Explicit sorts override and
    // flatten the grid into a single sorted list. Browse All (no explicit
    // sort) interleaves categories so the flat grid reads like an endless
    // store, varied products in every row instead of category clusters.
    if (sortBy === 'price-low') filtered.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-high') filtered.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name-az') filtered.sort((a, b) => a.name.localeCompare(b.name));
    else if (browseAll) filtered = stableShuffle(filtered);
    else filtered.sort((a, b) => (a.category || '').localeCompare(b.category || '') || a.name.localeCompare(b.name));

    setFilteredProducts(filtered);
  }, [products, filters, sortBy, browseAll]);

  const clearFilters = () => { setFilters({ category: '', priceRange: '', merchant: [], tags: [], searchQuery: '' }); setSortBy(''); setBrowseAll(false); setShowAIResults(false); };

  // Browse All is mutually exclusive with a category filter: turning it on
  // clears the category so the flat grid shows the entire catalog.
  const handleBrowseAllToggle = (val) => {
    setBrowseAll(val);
    if (val) setFilters(prev => ({ ...prev, category: '' }));
  };
  const removeFilter = (filterType, value) => {
    if (filterType === 'category') setFilters({...filters, category: ''});
    else if (filterType === 'priceRange') setFilters({...filters, priceRange: ''});
    else if (filterType === 'merchant') setFilters({...filters, merchant: filters.merchant.filter(m => m !== value)});
    else if (filterType === 'tag') setFilters({...filters, tags: filters.tags.filter(t => t !== value)});
    else if (filterType === 'sort') setSortBy('');
  };

  const handleAIRecommendations = (recommendations, personalitySummary = '', recipientName = '', creativeAlts = [], allMatches = []) => { 
    setShowAIResults(true); 
    setAiContext({ personalitySummary, recipientName });
    
    // Create a map of product_id to AI metadata (reason, confidence)
    const aiMap = new Map();
    recommendations.forEach(rec => {
      const product = rec.product || rec;
      if (product.id) {
        aiMap.set(product.id, {
          reason: rec.reason || '',
          confidence: rec.confidence || 'medium'
        });
      }
    });
    setAiRecommendationsMap(aiMap);
    
    // Set creative alternatives
    setCreativeAlternatives(creativeAlts);
    
    // Top picks first, then every other relevant match (ranked)
    const topPicks = recommendations.map(rec => rec.product || rec);
    const moreMatches = allMatches.map(m => m.product || m);
    const productList = [...topPicks, ...moreMatches];
    aiSeenIdsRef.current = new Set(productList.map(p => p.id));
    setFilteredProducts(productList); 
    
    // Show success toast with count
    toast.success(`✨ ${productList.length} personalized recommendations ready!`);
    
    window.scrollTo({ top: document.querySelector('[data-browse-content]')?.offsetTop - 100, behavior: 'smooth' }); 
  };

  const handleMoreLikeThis = async () => {
    if (filteredProducts.length === 0) {
      toast.error('No products to base recommendations on');
      return;
    }

    setLoadingMoreLikeThis(true);
    
    try {
      // Anchor on the visible top results, but exclude EVERYTHING already
      // shown this session so repeat clicks dig deeper instead of repeating.
      const productIds = filteredProducts.slice(0, 20).map(p => p.id);
      productIds.forEach(id => aiSeenIdsRef.current.add(id));
      const response = await api.post('/products/similar', {
        product_ids: productIds,
        exclude_ids: Array.from(aiSeenIdsRef.current),
      });
      
      // Update recommendations with new similar products
      const newRecommendations = response.data.recommendations;

      if (newRecommendations.length === 0) {
        toast.info("That's everything similar in the catalog — try different filters or clear results to browse all gifts.");
        return;
      }
      
      // Create new AI map
      const aiMap = new Map();
      newRecommendations.forEach(rec => {
        const product = rec.product || rec;
        if (product.id) {
          aiMap.set(product.id, {
            reason: rec.reason || '',
            confidence: rec.confidence || 'medium'
          });
        }
      });
      setAiRecommendationsMap(aiMap);
      
      // Update filtered products
      const productList = newRecommendations.map(rec => rec.product || rec);
      productList.forEach(p => aiSeenIdsRef.current.add(p.id));
      setFilteredProducts(productList);

      if (response.data.exhausted) {
        toast.info("You've nearly seen everything similar — this is the last batch.");
      }
      
      toast.success(`✨ Found ${productList.length} similar gifts!`);
      
    } catch (error) {
      logger.error('Error getting similar products:', error);
      toast.error('Failed to find similar products');
    } finally {
      setLoadingMoreLikeThis(false);
    }
  };

  // Check for AI recommendations from keepsake analysis (via navigation state)
  useEffect(() => {
    if (location.state?.aiRecommendations) {
      handleAIRecommendations(
        location.state.aiRecommendations,
        location.state.personalitySummary,
        location.state.recipientName
      );
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => { applyFilters(); }, [applyFilters]);
  // Selecting any category turns off Browse All (they're mutually exclusive,
  // a category is a sectioned/filtered view, Browse All is the flat catalog).
  useEffect(() => { if (filters.category) setBrowseAll(false); }, [filters.category]);
  useEffect(() => { const handleScroll = () => { setIsScrolled(window.scrollY > 300); }; window.addEventListener('scroll', handleScroll); return () => window.removeEventListener('scroll', handleScroll); }, []);

  const hasActiveFilters = filters.searchQuery || filters.category || filters.priceRange || filters.merchant.length > 0 || filters.tags.length > 0;

  // ---- Admin "Browse Sections" → browse page grouping -------------------
  // Maps raw product categories (and tag matches) onto the admin-defined
  // display sections so the storefront mirrors Admin → Categories → Browse
  // Sections. Products with no matching section fall back to their raw
  // category name.
  const sectionMaps = useMemo(() => {
    const active = (browseSections || []).filter((s) => s.is_active !== false);
    const catToSection = {};
    const meta = {};
    const order = {};
    active.forEach((s, i) => {
      meta[s.title] = { description: s.description, categories: s.categories || [], tags: s.tags || [] };
      order[s.title] = s.order ?? i;
      (s.categories || []).forEach((c) => {
        if (!(c in catToSection)) catToSection[c] = s.title;
      });
    });
    return { catToSection, meta, order, hasSections: active.length > 0 };
  }, [browseSections]);

  const groupedProducts = useMemo(() => {
    if (!sectionMaps.hasSections) return filteredProducts;
    const keyed = filteredProducts.map((p) => {
      let key = sectionMaps.catToSection[p.category];
      if (!key) {
        const tagged = Object.entries(sectionMaps.meta).find(
          ([, m]) => m.tags.length > 0 && (p.tags || []).some((t) => m.tags.includes(t))
        );
        key = tagged ? tagged[0] : null;
      }
      return { ...p, __section: key || p.category, __order: key ? sectionMaps.order[key] : 999 };
    });
    return keyed
      .slice()
      .sort((a, b) => a.__order - b.__order || a.__section.localeCompare(b.__section));
  }, [filteredProducts, sectionMaps]);
  
  // Memoize expensive computations
  const categories = useMemo(() => {
    return [...new Set(products.map(p => p.category))];
  }, [products]);
  
  const priceRanges = [{ value: '0-25', label: 'Under $25' }, { value: '25-50', label: '$25 - $50' }, { value: '50-100', label: '$50 - $100' }, { value: '100-', label: '$100+' }];
  
  const merchants = useMemo(() => {
    return [...new Set(products.map(p => p.merchant))].filter(Boolean);
  }, [products]);
  
  const allTags = useMemo(() => {
    return [...new Set(products.flatMap(p => p.tags || []))].filter(Boolean).sort();
  }, [products]);

  return (
    <div className="transition-colors page-enter" onClick={deckState.closeFannedDeck} style={{background: darkMode.bgPrimary, overflowX: 'clip'}}>
      <div className="min-h-screen transition-colors" style={{background: darkMode.bgPrimary, maxWidth: '100vw', overflowX: 'clip'}}>
        {prelaunch ? (
          <>
            <ComingSoon user={user} />
            <Footer />
          </>
        ) : (
        <>
        <HeroSection
          onResults={handleAIRecommendations}
          onCreateQuestionnaire={() => {
            if (user) {
              window.location.href = '/keepsakes/create';
            } else {
              window.dispatchEvent(new CustomEvent('mementa:open-auth'));
              toast.info('Sign in to create a gift questionnaire');
            }
          }}
        />
        <StickyFilterBar isScrolled={isScrolled} filters={filters} setFilters={setFilters} sortBy={sortBy} setSortBy={setSortBy} showFilters={showFilters} setShowFilters={setShowFilters} hasActiveFilters={hasActiveFilters} products={products} categories={categories} allTags={allTags} browseAll={browseAll} onToggleBrowseAll={handleBrowseAllToggle} archetypes={visibleArchetypes} activeArchetype={activeArchetype} onArchetypeChange={handleArchetypeChange}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6" style={{borderTop: (hasActiveFilters || sortBy || browseAll || showFilters) ? `1px solid ${darkMode.borderPrimary}` : 'none'}}>
            {(hasActiveFilters || sortBy || browseAll) && <div className="py-3"><ActiveFilters filters={filters} sortBy={sortBy} removeFilter={removeFilter} clearFilters={clearFilters} sortOptions={sortOptions} showBrowseAll={browseAll} onClearBrowseAll={() => setBrowseAll(false)} /></div>}
            <FilterPanel showFilters={showFilters} setShowFilters={setShowFilters} filters={filters} setFilters={setFilters} categories={categories} priceRanges={priceRanges} merchants={merchants} allTags={allTags} hasActiveFilters={hasActiveFilters} clearFilters={clearFilters} removeFilter={removeFilter} onAIClick={() => { setShowFilters(false); window.dispatchEvent(new CustomEvent('mementa:focus-genie')); }} sortBy={sortBy} setSortBy={setSortBy} products={products} />
          </div>
        </StickyFilterBar>

        <QuestionnaireFilterBar
          user={user}
          activeName={showAIResults ? aiContext.recipientName : ''}
          onClear={() => { setShowAIResults(false); applyFilters(); }}
          onLoadingChange={setLoadingPreferences}
          onResults={handleAIRecommendations}
        />

        {/* Unified content area, one layout for every state. AI results
            swap the EditorialRail for the AIResultsHeader; everything
            else stays consistent so filters/sorts just refine the same
            grid in-place. */}
        <div className="max-w-7xl mx-auto px-4 py-8" data-browse-content>
          {/* Non-Curated archetype worlds render their own hero + content
              and short-circuit the standard grid. Curated keeps 100% of
              the existing Browse layout untouched. */}
          {worldsEnabled && activeArchetype !== 'curated' && !showAIResults ? (
            <>
              <ArchetypeWorldHeader archetype={currentArchetype} />
              <ArchetypeWorld archetype={currentArchetype} />
            </>
          ) : productsLoading && products.length === 0 ? (
            <div data-testid="browse-loading-skeleton">
              <GridSkeleton count={12} />
            </div>
          ) : showAIResults ? (
            <>
              <AIResultsHeader
                aiContext={aiContext}
                loadingMoreLikeThis={loadingMoreLikeThis}
                handleMoreLikeThis={handleMoreLikeThis}
                filteredProducts={filteredProducts}
                setFilteredProducts={setFilteredProducts}
                setShowAIResults={setShowAIResults}
                setAiRecommendationsMap={setAiRecommendationsMap}
                setAiContext={setAiContext}
                applyFilters={applyFilters}
                user={user}
                darkMode={darkMode}
              />
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    user={user}
                    source="browse"
                    aiMetadata={aiRecommendationsMap.get(product.id)}
                  />
                ))}
              </div>
              {creativeAlternatives.length > 0 && (
                <div className="mt-16">
                  <div className="mb-6">
                    <h2 className="text-3xl mb-2 transition-colors" style={{fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: '600'}}>Worth Considering</h2>
                    <p className="text-sm transition-colors" style={{color: darkMode.textSecondary}}>A few creative picks that might surprise and delight</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {creativeAlternatives.map(alt => {
                      const product = alt.product || alt;
                      return (
                        <ProductCard
                          key={product.id}
                          product={product}
                          user={user}
                          source="browse"
                          aiMetadata={{ reason: alt.reason || '', confidence: alt.confidence || 'medium' }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
          <>
              {/* Discovery surfaces (Curated for you rail + spotlight) hide
                  while filters are active so a filtered Results view never
                  mixes in unrelated featured collections. */}
              {!hasActiveFilters && (
              <>
              <EditorialRail
                featuredSections={featuredSections}
                darkMode={darkMode}
                activeFeaturedId={spotlightFeatured?.id}
                onFeaturedClick={(featured) => {
                  // Toggle: clicking the active tile collapses the
                  // spotlight; clicking a different tile swaps it in.
                  if (spotlightFeatured?.id === featured.id) {
                    setSpotlightFeatured(null);
                    return;
                  }
                  setSpotlightFeatured(featured);
                  // Scroll the spotlight into view after the render
                  // commit so the user sees the change happen.
                  setTimeout(() => {
                    document
                      .querySelector('[data-testid="featured-spotlight"]')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }}
              />

              {/* Spotlighted featured section, opens when a user clicks a
                  tile in the editorial rail above. Sits directly below the
                  rail so the relationship between the clicked tile and
                  its expanded content is visually obvious. Dismissable
                  via the close button.

                  `scrollMarginTop` keeps the spotlight from getting tucked
                  under the sticky nav + filter bar when scrollIntoView()
                  fires, without it the close button lands behind the nav
                  and clicks pass through to the wrong element. */}
              {spotlightFeatured && (() => {
                const rendered = renderFeaturedSection(spotlightFeatured, 'spotlight');
                if (!rendered) return null;
                return (
                  <div
                    className="mb-8 relative"
                    data-testid="featured-spotlight"
                    style={{ scrollMarginTop: '200px' }}
                  >
                    {rendered}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSpotlightFeatured(null);
                      }}
                      className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all hover:scale-105 hover:transform-none"
                      style={{
                        background: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(8px)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        zIndex: 60
                      }}
                      aria-label="Close spotlight"
                      data-testid="featured-spotlight-close"
                    >
                      <span aria-hidden="true">×</span>
                      <span>Close</span>
                    </button>
                  </div>
                );
              })()}
              </>
              )}
              {(hasActiveFilters || sortBy) && (
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl mb-1 transition-colors" style={{fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: '600'}}>Results</h2>
                  <p className="text-sm transition-colors" style={{color: darkMode.textSecondary}}>{filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'} match your filters</p>
                </div>
              )}
              <UnifiedProductGrid
                products={(!filters.category && !sortBy && !browseAll) ? groupedProducts : filteredProducts}
                allProducts={products}
                featuredSections={featuredSections}
                decks={decks}
                groupByCategory={!filters.category && !sortBy && !browseAll}
                browseAll={browseAll}
                sectionMeta={sectionMaps.meta}
                categoryKeyMap={sectionMaps.catToSection}
                renderFeaturedSection={renderFeaturedSection}
                filters={filters}
                setFilters={setFilters}
                deckState={deckState}
                user={user}
                darkMode={darkMode}
                scrollAnchors={scrollAnchors}
              />
            </>
          )}
        </div>
        <Footer />
        </>
        )}
      </div>

      {/* Loading overlay for preferences search */}
      {loadingPreferences && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-[#FED7A5] mb-4"></div>
            <p className="text-white text-lg font-medium">Finding perfect gifts based on preferences...</p>
            <p className="text-white/60 text-sm mt-2">This may take a moment</p>
          </div>
        </div>
      )}

      {deckState.selectedProduct && (
        <ProductModal
          product={deckState.selectedProduct}
          onClose={deckState.closeProductModal}
          user={user}
          aiMetadata={aiRecommendationsMap.get(deckState.selectedProduct.id)}
        />
      )}
    </div>
  );
}
